/**
 * Corpus statistics, phrased for customers.
 *
 * Meeting and town totals change daily. They are read live from Atlas and **rounded down**
 * before display — "over 245,000 meetings" — so the copy never needs updating as the real
 * numbers grow, and is never an overstatement (BUSINESS_RULES § Data sources, rule 4).
 *
 * Rounding down is the whole point: rounding to nearest would occasionally claim more than
 * the corpus holds. Never print an exact count in customer-facing copy — it ages badly and
 * invites a reader to check it.
 *
 * Shared by Search's empty state and both onboarding screens, so the two never disagree.
 */

export interface CorpusStats {
  meetings: number;
  towns: number;
  countries: number;
}

function floorTo(value: number, step: number): string {
  return (Math.floor(value / step) * step).toLocaleString('en-US');
}

/** "over 245,000" — meetings round down to the nearest 5,000. */
export function sayMeetings(stats: CorpusStats): string {
  return `over ${floorTo(stats.meetings, 5_000)}`;
}

/**
 * "over 3,800" — rounded down to the nearest 100.
 *
 * Counts JURISDICTIONS, not towns: the `cities` table also holds county boards and state
 * agencies, which are places you can follow but are not towns. The function keeps its name for
 * call-site familiarity; the copy it feeds says "places".
 */
export function sayTowns(stats: CorpusStats): string {
  return `over ${floorTo(stats.towns, 100)}`;
}

export function sayCountries(stats: CorpusStats): string {
  return String(stats.countries);
}
